import { type ComponentProps, memo, useCallback } from 'react';
import type {
  ShareFiltersInput,
  ShareOrderFieldEnum,
} from '@frameio/graphql-client/generated';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { IconButton2, Menu } from '@frameio/vapor';
import { IconSort } from '@frameio/vapor-icons';
import { useSharesFilterBy } from '../../../../hooks/useSharesFilterBy';
import { useSharesSortBy } from '../../../../hooks/useSharesSortBy';
import TEXT from './generated/TEXT/SharesButton';
import { SharesMenuContent } from './SharesMenuContent';

const MENU_WIDTH = '200px';

type Props = {
  buttonVariant?: ComponentProps<typeof IconButton2>['variant'];
};

export const SharesButton = memo(({ buttonVariant = 'tertiary' }: Props) => {
  const [sortBy, setSortBy] = useSharesSortBy();
  const [filterBy, setFilterBy] = useSharesFilterBy();
  const track = useTrackUnsafe();

  const handleSortByFieldSelect = useCallback(
    (field: ShareOrderFieldEnum) => {
      setSortBy({ field, order: sortBy.order });
      track('share-links-sorted', {
        field,
        direction: sortBy.order,
      });
    },
    [setSortBy, sortBy.order, track]
  );

  const handleToggleSortByOrder = useCallback(() => {
    setSortBy({
      field: sortBy.field,
      order: sortBy.order === 'ASC' ? 'DESC' : 'ASC',
    });
  }, [setSortBy, sortBy.order, sortBy.field]);

  const handleFilterByFieldSelect = useCallback(
    (value: ShareFiltersInput) => {
      setFilterBy({
        ...filterBy,
        ...value,
      });
      track('share-links-filtered', {
        by_creator: value.creatorId ? 'me' : 'all',
      });
    },
    [filterBy, setFilterBy, track]
  );

  if (!sortBy) {
    return null;
  }

  return (
    <Menu
      autoHide={false}
      content={
        <SharesMenuContent
          filterBy={filterBy}
          onFilterByFieldSelect={handleFilterByFieldSelect}
          onSortByFieldSelect={handleSortByFieldSelect}
          onToggleSortByOrder={handleToggleSortByOrder}
          sortBy={sortBy}
        />
      }
      minWidth={MENU_WIDTH}
      placement="bottom-start"
    >
      <IconButton2
        data-dd-action-name="shares.sort-filter"
        icon={<IconSort />}
        label={TEXT.moreButtonLabel}
        size="small"
        variant={buttonVariant}
      />
    </Menu>
  );
});

TEXT.DEFINE({
  /** Icon button aria-label for shares sort/filter menu */
  moreButtonLabel: 'Sort & Filter Shares',
});
